import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './MultiInput.module.css';
import CreatableSelect from 'react-select/creatable';
import _ from 'lodash';

/**
 * MultiInput
 * @type React.FC<MultiInputPropTypes>
 */
const MultiInput = React.forwardRef((props, ref) => {
  const refInputMulti = useRef();

  const components = {
    DropdownIndicator: null,
  };

  const createOption = (label = '') => ({
    label,
    value: label,
  });

  const [inputValue, setInputValue] = useState('');
  const [value, setValue] = useState(props.defaultValue);

  const handleKeyDown = (event) => {
    if (!inputValue) return;
    switch (event.key) {
      case 'Enter':
      case 'Tab':
        const _value = _.cloneDeep(value).concat(createOption(inputValue));

        setValue(_removeDuplicates(_value));
        setInputValue('');
        event.preventDefault();
    }
  };

  const [currState, setCurrState] = useState({
    defaultValue: props.defaultValue,
    placeholder: props.placeholder,
    isClearable: props.isClearable,
    disabled: props.disabled,
    isMulti: props.isMulti,
    action: props.action,
  });

  Object.keys(props).forEach((p) => {
    if (!Object.keys(MultiInputPropTypes).includes(p)) {
      console.error(`MultiInput: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    /**
     * value(값) 리턴
     * @returns
     */
    getValue() {
      if (currState.isMulti) {
        return value.map((item) => item.value);
      } else {
        return value.length > 0 ? value[0].value : '';
      }
    },
    /**
     * label(값) 리턴
     * @returns
     */
    getLabel() {
      if (currState.isMulti) {
        return value.map((item) => item.label);
      } else {
        return value.length > 0 ? value[0].label : '';
      }
    },
    /**
     * value(값) 리턴
     * @returns
     */
    getValueObject() {
      return value;
    },
    /**
     * value(값) 세팅
     * @param {*} v
     */
    setValue(v) {
      if (currState.isMulti) {
        const _value = _.cloneDeep(value).concat(v);
        setValue(_removeDuplicates(_value));
      } else {
        setValue(v);
      }
    },
    /**
     * 첫번째 값 또는 기본 값으로 되돌리기
     */
    clear() {
      setValue(currState.defaultValue);
    },
    /**
     * enable
     */
    enable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: false,
      }));
    },
    /**
     * disable
     */
    disable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: true,
      }));
    },

    updateState(stateName, newValue) {
      // currState에 있는 값만 업데이트 가능
      if (Object.keys(currState).includes(stateName)) {
        setCurrState((prev) => ({
          ...prev,
          [stateName]: newValue,
        }));
      } else {
        console.error(
          'MultiInput: State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다.'
        );
      }
    },
  }));

  const _onChange = (newValue) => {
    setValue(newValue);
    if (props.onChange) props.onChange;
  };

  const _onInputChange = (newValue) => {
    if (currState.action) setInputValue(newValue);
    if (props.onInputChange) props.onInputChange;
  };

  /**
   * 중복제거
   * @param {*} arr
   * @returns
   */
  const _removeDuplicates = (arr = []) => {
    return _.uniqBy(arr, 'value');
  };

  return (
    <div className={`${props.controlWidth ?? ''}`} style={{ minWidth: '100%' }}>
      <CreatableSelect
        ref={refInputMulti}
        components={components}
        inputValue={inputValue}
        isClearable={currState.isClearable}
        isDisabled={currState.disabled}
        isMulti={currState.isMulti}
        menuIsOpen={false}
        onChange={_onChange}
        onInputChange={_onInputChange}
        onKeyDown={handleKeyDown}
        placeholder={currState.placeholder ?? ''}
        value={value}
      />
    </div>
  );
});

const MultiInputPropTypes = {
  defaultValue: PropTypes.array,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onInputChange: PropTypes.func,
  controlWidth: PropTypes.string,
  isClearable: PropTypes.bool,
  placeholder: PropTypes.string,
  isMulti: PropTypes.bool,
  action: PropTypes.bool,
};

MultiInput.propTypes = MultiInputPropTypes;
MultiInput.defaultProps = {
  defaultValue: [],
  disabled: false,
  onChange: null,
  onInputChange: null,
  controlWidth: null,
  isClearable: false,
  placeholder: '',
  isMulti: true,
  action: false,
};
MultiInput.displayName = 'MultiInput';
export default MultiInput;
